'use client';

import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

import classes from './modal.module.css';

/**
 * The modal's action-row element: `undefined` outside a `Modal`, `null` inside one whose slot
 * hasn't attached yet. Telling those apart keeps {@link ModalFooter} from rendering its children
 * in place on the first pass and re-mounting them into the portal on the second. See modal.tsx.
 */
const ModalFooterSlotContext = createContext<HTMLDivElement | null | undefined>(undefined);

export const ModalFooterSlotProvider = ModalFooterSlotContext.Provider;

/**
 * ### ModalFooter
 *
 * @description
 * Renders `children` in the fixed action row of the surrounding `Modal`, from wherever it sits in
 * the tree — the row has to be a sibling of the scrolling region, but the components that fill it
 * are usually several levels inside a content component that doesn't own the `Modal`.
 *
 * Outside a `Modal` — in a `Drawer`, say, which has no such row — it renders in place, with the
 * spacing the fixed row would have provided.
 *
 * @param props - children rendered into the modal's action row
 * @returns React component
 */
export const ModalFooter = ({ children }: { children: ReactNode }) => {
  const slot = useContext(ModalFooterSlotContext);

  if (slot === undefined) return <div className={classes.inlineFooter}>{children}</div>;

  return slot && createPortal(children, slot);
};
